/**
 * SurfaceMediaCard — `.md.md--img`, the card the right column is built from.
 *
 * Built to `publisher-drop-hub.html` lines 1000-1026: the live-creator card and
 * the reveal-trailer card are the SAME component at the same footprint, which
 * is why an activation (a playable ad, a leaderboard) can take a position in
 * that column and read as though it was always there. The anchor states the
 * rule directly — activations take positions, they do not get a region.
 *
 * ## A live badge and a header row cannot both own the corners
 *
 * `.lvb` is absolute at top-left and `.md__k` is the left item of `.md__hd`, so
 * on a card with both, "● Live" printed directly on top of "Official creator",
 * and the viewer count on top of the platform name in the opposite corner —
 * both corners, both cards, every anchor. The badges keep the corners because
 * live state is the most urgent thing on the card, and the header drops below
 * them. The spine does that with `:has(.lvb)`, so it only applies to cards that
 * actually have a badge.
 *
 * ## `flex: 1.3 1 auto`, and the `auto` is the load-bearing part
 *
 * `flex:1.3` is shorthand for `1.3 1 0%` — a ZERO basis, so the card
 * contributes nothing to the column's content height and the column never grows
 * to hold it. With a quest panel and an ad in there too, the image cards were
 * handed what was left of a band-height column, which was nothing, and their
 * titles and play buttons landed on top of each other. An `auto` basis starts
 * each card at its own content height and grow only shares out what is left.
 * That is in the ported spine; it is repeated here because it is the reason
 * this component must not be given a fixed height by a caller.
 */

import React from "react";
import {safeCssUrl} from "@/widgets/play/safeHref";

export interface SurfaceMediaCardProps {
    /** Artwork behind the card. Absent leaves the panel ground showing. */
    imageUrl?: string;
    /** Top-left badge. Reserved for genuinely live content. */
    live?: boolean;
    /** Top-right, beside the badge — "190K watching". Pre-formatted. */
    liveMeta?: string;
    /** Header kicker, in the accent — "Official creator", "Reveal trailer". */
    kicker?: string;
    /** Header right — the platform, or a duration. */
    kickerMeta?: string;
    title: string;
    description?: string;
    /** Small-caps line under the description. */
    meta?: string;
    /** Monogram faces along the bottom, with a trailing label. */
    faces?: ReadonlyArray<{ id: string; initials: string }>;
    facesLabel?: string;
    /** Renders the circular play control over the artwork. */
    playable?: boolean;
    onClick?: () => void;
    href?: string;
}

export const SurfaceMediaCard: React.FC<SurfaceMediaCardProps> = ({
    imageUrl, live, liveMeta, kicker, kickerMeta, title, description, meta,
    faces = [], facesLabel, playable, onClick, href,
}) => {
    const body =
        <>
            {imageUrl && <span className="sh-md__bg" style={{backgroundImage: safeCssUrl(imageUrl)}} />}
            <span className="sh-md__sc" />

            {live && <span className="sh-lvb">● Live</span>}
            {live && liveMeta && <span className="sh-lvv">{liveMeta}</span>}

            {(kicker || kickerMeta) &&
                <div className="sh-md__hd">
                    {kicker && <span className="sh-md__k">{kicker}</span>}
                    {/*
                      * When there is no badge, the count belongs in the header's
                      * right slot rather than floating in the corner — the corner
                      * position exists to sit beside a badge, and on its own it
                      * reads as a stray number.
                      */}
                    <span className="sh-md__x">{kickerMeta ?? (!live ? liveMeta : undefined)}</span>
                </div>}

            {playable &&
                <span className="sh-md__play">
                    <u>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </u>
                </span>}

            <div className="sh-md__c">
                <span className="sh-md__t">{title}</span>
                {description && <span className="sh-md__d">{description}</span>}
                {meta && <span className="sh-md__m">{meta}</span>}
                {faces.length > 0 &&
                    <div className="sh-md__f">
                        {faces.map(f => <i key={f.id}>{f.initials}</i>)}
                        {facesLabel && <b>{facesLabel}</b>}
                    </div>}
            </div>
        </>;
    const className = "sh-md sh-md--img";

    /*
     * An anchor only when there is somewhere to go. A card that navigates is a
     * link and must be one — keyboard reachable, openable in a new tab — but
     * wrapping a non-navigating card in an `<a href="#">` announces a
     * destination that does not exist.
     */
    if (href) {
        return <a className={className} href={href} target="_blank" rel="noreferrer">{body}</a>;
    }
    if (onClick) {
        return <button type="button" className={className} onClick={onClick}>{body}</button>;
    }
    return <div className={className}>{body}</div>;
};

export default SurfaceMediaCard;
